import { type ChangeEvent, useRef, useState } from "react";
import { useParams } from "react-router";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ALLOWED_CONTENT_TYPES = new Set([
	"image/jpeg",
	"image/png",
	"image/gif",
	"image/webp",
	"image/svg+xml",
	"application/pdf",
	"text/plain",
	"text/csv",
]);

type UploadState = "idle" | "uploading" | "success" | "error";

export function FileUpload() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const inputRef = useRef<HTMLInputElement>(null);

	const [state, setState] = useState<UploadState>("idle");
	const [error, setError] = useState("");
	const [successName, setSuccessName] = useState("");
	const [uploadedFileId, setUploadedFileId] = useState("");

	async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;

		setError("");
		setSuccessName("");
		setUploadedFileId("");

		if (file.size > MAX_FILE_SIZE) {
			setState("error");
			setError("File too large (max 10MB)");
			if (inputRef.current) inputRef.current.value = "";
			return;
		}

		if (!ALLOWED_CONTENT_TYPES.has(file.type)) {
			setState("error");
			setError(`Content type "${file.type || "unknown"}" is not allowed`);
			if (inputRef.current) inputRef.current.value = "";
			return;
		}

		setState("uploading");

		const formData = new FormData();
		formData.append("file", file);

		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/files`, {
				method: "POST",
				credentials: "include",
				headers: { Origin: window.location.origin },
				body: formData,
			});

			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setState("error");
				setError(
					(body as { error?: string } | null)?.error ??
						`Upload failed (${res.status})`,
				);
				return;
			}

			const body = (await res.json()) as { id: string; name: string };
			setState("success");
			setSuccessName(body.name);
			setUploadedFileId(body.id);
		} catch {
			setState("error");
			setError("Network error");
		} finally {
			if (inputRef.current) inputRef.current.value = "";
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg">Upload File</CardTitle>
				<CardDescription>
					Max 10MB. Allowed: images, PDF, plain text, CSV.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<div className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="file-upload">File</Label>
						<input
							ref={inputRef}
							id="file-upload"
							type="file"
							data-testid="file-input"
							className="hidden"
							onChange={handleFileChange}
						/>
						<div>
							<Button
								type="button"
								data-testid="upload-button"
								disabled={state === "uploading"}
								onClick={() => inputRef.current?.click()}
							>
								{state === "uploading" ? "Uploading\u2026" : "Choose File"}
							</Button>
						</div>
					</div>

					{state === "error" && error && (
						<p data-testid="upload-error" className="text-sm text-destructive">
							{error}
						</p>
					)}

					{state === "success" && (
						<p
							data-testid="upload-success"
							data-file-id={uploadedFileId}
							className="text-sm text-green-600"
						>
							Uploaded: {successName}
						</p>
					)}
				</div>
			</CardContent>
		</Card>
	);
}
